import { mergeSlotProps, useForkRef } from '@mui/material/utils';
import { DesktopDatePicker, DesktopDatePickerProps } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { type FormEvent, type KeyboardEvent, useCallback, useRef, useState } from 'react';
import { asUTCDate, parseDate } from 'shared/utils/DateTime';

interface QuickDatePickerProps extends Omit<DesktopDatePickerProps, 'onChange' | 'value'> {
  /** Open the picker when the input receives focus */
  openOnFocus?: boolean;
  onChange: (value: string | null) => void;
  value?: string | DateTime | Date | null;
}

type RawInput = { controlledDateKey: string | null; value: string };

type QuickDatePickerSlotProps = NonNullable<DesktopDatePickerProps['slotProps']>;
type CalendarHeaderSlotProps = NonNullable<QuickDatePickerSlotProps['calendarHeader']>;
type NavigationButtonSlotProps = NonNullable<QuickDatePickerSlotProps['previousIconButton']>;
type NavigationIconSlotProps = NonNullable<QuickDatePickerSlotProps['leftArrowIcon']>;
type OpenPickerButtonSlotProps = NonNullable<QuickDatePickerSlotProps['openPickerButton']>;

const V5_CALENDAR_HEADER_SLOT_PROPS: CalendarHeaderSlotProps = {
  sx: { marginBottom: '8px', marginTop: '16px', maxHeight: '30px', minHeight: '30px' },
};
const V5_NAVIGATION_BUTTON_SLOT_PROPS: NavigationButtonSlotProps = { size: 'small' };
const V5_NAVIGATION_ICON_SLOT_PROPS: NavigationIconSlotProps = { fontSize: 'medium' };
const KEYBOARD_FOCUS_VISIBLE_OPEN_PICKER_SLOT_PROPS: OpenPickerButtonSlotProps = {
  sx: { '&:focus-visible': { outline: '2px solid var(--color-brand-6)', outlineOffset: '2px' } },
};

export default function QuickDatePicker({
  inputRef,
  onChange,
  openOnFocus,
  slotProps,
  value,
  ...props
}: QuickDatePickerProps) {
  const [open, setOpen] = useState(false);
  const [pickerInputElement, setPickerInputElement] = useState<HTMLInputElement | null>(null);
  const justClosedRef = useRef(false);
  const rawInputRef = useRef<RawInput | null>(null);
  const handleInputRef = useForkRef(inputRef, setPickerInputElement);
  const controlledDate = asUTCDate(value);
  const controlledDateKey = controlledDate?.toISODate() ?? null;

  const handleClose = useCallback(() => {
    justClosedRef.current = true;
    setOpen(false);
    // Reset after a short delay to allow focus events to settle
    setTimeout(() => {
      justClosedRef.current = false;
    }, 100);
  }, []);

  const handleFocusOpen = useCallback(() => {
    // Don't reopen if we just closed (prevents click-away immediately reopening)
    if (!justClosedRef.current) {
      setOpen(true);
    }
  }, []);

  const handleChange = (nextValue: DateTime | null) => {
    if (nextValue === null) {
      rawInputRef.current = null;
      onChange(null);
      return;
    }

    // set zone to UTC to ensure server does not offset time
    const date = parseDate(nextValue)?.setZone('utc', { keepLocalTime: true });

    if (!date?.isValid) {
      return;
    }

    rawInputRef.current = null;
    onChange(date.toISO());
  };

  const handleInputCapture = (event: FormEvent<HTMLElement>) => {
    if (event.target instanceof HTMLInputElement) {
      rawInputRef.current = { controlledDateKey, value: event.target.value };
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // move cursor forward if last character is a slash
    if (event.key === '/') {
      const input = event.currentTarget;
      const { selectionStart, selectionEnd } = input;

      if (selectionStart === null || selectionEnd === null) {
        return;
      }

      const noSelection = selectionStart === selectionEnd;
      const lastChar = input.value[input.value.length - 1];
      const cursorBeforeEnd = selectionStart === input.value.length - 1;

      if (noSelection && lastChar === '/' && cursorBeforeEnd) {
        input.setSelectionRange(selectionStart + 1, selectionEnd + 1);
        event.preventDefault();
      }
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      const input = event.currentTarget;
      const rawInput = rawInputRef.current;
      const inputValue = rawInput?.controlledDateKey === controlledDateKey ? rawInput.value : input.value;
      const [first, second, third] = inputValue.split('/');
      let day: string;
      let month: string | undefined;
      // since we're using user input, we want to match with user's local time.
      // it'll be converted to a zero'd out UTC date object via onChange
      let assumed = DateTime.local();

      if (third) {
        if (third.length === 2) {
          assumed = assumed.set({ year: parseInt(`20${third}`) });
        } else {
          return;
        }
      }

      if (!second) {
        day = first;
      } else {
        month = first;
        day = second;
      }

      assumed = assumed.set({ day: parseInt(day) });

      if (month) {
        assumed = assumed.set({ month: parseInt(month) });
      }

      handleChange(assumed);
    }
  };

  return (
    <DesktopDatePicker
      {...props}
      value={controlledDate}
      format="MM/dd/yyyy"
      inputRef={handleInputRef}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={handleClose}
      slotProps={{
        ...slotProps,
        calendarHeader: mergeSlotProps<CalendarHeaderSlotProps, CalendarHeaderSlotProps>(
          slotProps?.calendarHeader ?? {},
          V5_CALENDAR_HEADER_SLOT_PROPS
        ),
        leftArrowIcon: mergeSlotProps<NavigationIconSlotProps, NavigationIconSlotProps>(
          slotProps?.leftArrowIcon ?? {},
          V5_NAVIGATION_ICON_SLOT_PROPS
        ),
        nextIconButton: mergeSlotProps<NavigationButtonSlotProps, NavigationButtonSlotProps>(
          slotProps?.nextIconButton ?? {},
          V5_NAVIGATION_BUTTON_SLOT_PROPS
        ),
        openPickerButton: mergeSlotProps<OpenPickerButtonSlotProps, OpenPickerButtonSlotProps>(
          slotProps?.openPickerButton ?? {},
          KEYBOARD_FOCUS_VISIBLE_OPEN_PICKER_SLOT_PROPS
        ),
        textField: mergeSlotProps(slotProps?.textField ?? {}, {
          ...(open ? { focused: false } : {}),
          onFocus: openOnFocus ? handleFocusOpen : undefined,
          onInputCapture: handleInputCapture,
          onKeyDown: handleKeyDown,
        }),
        popper: mergeSlotProps(slotProps?.popper ?? {}, {
          anchorEl: pickerInputElement,
          modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
          placement: 'bottom',
        }),
        previousIconButton: mergeSlotProps<NavigationButtonSlotProps, NavigationButtonSlotProps>(
          slotProps?.previousIconButton ?? {},
          V5_NAVIGATION_BUTTON_SLOT_PROPS
        ),
        rightArrowIcon: mergeSlotProps<NavigationIconSlotProps, NavigationIconSlotProps>(
          slotProps?.rightArrowIcon ?? {},
          V5_NAVIGATION_ICON_SLOT_PROPS
        ),
      }}
      onChange={handleChange}
    />
  );
}
